import { useState } from "react";
import { CheckIcon } from "../components/icons";
import finhomesLogo from "../assets/finhomes-logo.png";
import { login, requestMobileOtp, verifyMobileOtp } from "../api/authApi";
import { isApiSuccess } from "../utils/apiResponse";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CC_OPTIONS = ["+63"];

export default function LoginPage({ onOpenJoin, onOpenForgot, onSubmit }) {
  const [authMode, setAuthMode] = useState("email");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [cc, setCc] = useState("+63");
  const [mobileNo, setMobileNo] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [mobileSubmitted, setMobileSubmitted] = useState(false);
  const [mobileSubmitting, setMobileSubmitting] = useState(false);
  const [mobileApiError, setMobileApiError] = useState("");

  const emailOk = EMAIL_RE.test(email);
  const pwdOk = pwd.trim().length > 0;
  const mobileOk = /^9\d{9}$/.test(mobileNo.replace(/\s/g, ""));
  const otpOk = otp.length === 6;

  const submitEmail = async () => {
    setSubmitted(true);
    setApiError("");
    if (!emailOk || !pwdOk) return;

    setSubmitting(true);
    try {
      const { data } = await login(email, pwd);
      if (!isApiSuccess(data)) {
        setApiError(data?.message || "Invalid email or password");
        return;
      }
      onSubmit(data);
    } catch (err) {
      setApiError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMobile = async () => {
    setMobileSubmitted(true);
    setMobileApiError("");
    if (!mobileOk) return;

    setMobileSubmitting(true);
    try {
      const { data } = await requestMobileOtp(cc, mobileNo);
      if (!isApiSuccess(data)) {
        setMobileApiError(data?.message || "Could not send OTP");
        return;
      }
      setOtpSent(true);
      setMobileSubmitted(false);
    } catch (err) {
      setMobileApiError(err.response?.data?.message || "Could not send OTP");
    } finally {
      setMobileSubmitting(false);
    }
  };

  const submitVerifyOtp = async () => {
    setMobileSubmitted(true);
    setMobileApiError("");
    if (!otpOk) return;

    setMobileSubmitting(true);
    try {
      const { data } = await verifyMobileOtp(cc, mobileNo, otp);
      if (!isApiSuccess(data)) {
        setMobileApiError(data?.message || "Invalid OTP");
        return;
      }
      onSubmit(data);
    } catch (err) {
      setMobileApiError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setMobileSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-col">
        <div className="auth-card-top">
          <div className="auth-card-top__row">
            <span className="auth-card-top__spacer" />
            <button type="button" className="btn-link" onClick={onOpenJoin}>
              Join Us
            </button>
          </div>
          <div className="auth-card-top__logo-row">
            <img src={finhomesLogo} alt="finhomes by Carmudi" className="auth-card-top__logo" />
          </div>
        </div>

        <div className="auth-card">
          <h1>Login</h1>

          {authMode === "email" ? (
            <div>
              {apiError && <div className="auth-error-banner">{apiError}</div>}
              <div className="email-field">
                <span className="email-field__label">Email</span>
                <input
                  className={"email-field__input" + (submitted && !emailOk ? " input-invalid" : "")}
                  type="email"
                  autoComplete="username"
                  placeholder="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailOk && (
                  <span className="email-field__check">
                    <CheckIcon size={18} width={2.4} />
                  </span>
                )}
                {submitted && !emailOk && <span className="field-error">Enter a valid email address</span>}
              </div>
              <div className="password-field">
                <span className="password-field__label">Password</span>
                <input
                  className={"password-field__input" + (submitted && !pwdOk ? " input-invalid" : "")}
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                />
                <button
                  type="button"
                  className="password-field__toggle"
                  aria-label="Show password"
                  onClick={() => setShowPwd((v) => !v)}
                >
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                    {!showPwd && <line x1="3" y1="21" x2="21" y2="3"></line>}
                  </svg>
                </button>
                {submitted && !pwdOk && <span className="field-error">Password is required</span>}
              </div>
              <div className="auth-row">
                <button type="button" className="btn-link" onClick={onOpenForgot}>
                  Forgot Password
                </button>
              </div>
              <button type="button" className="btn-primary" onClick={submitEmail} disabled={submitting}>
                {submitting ? "Logging in…" : "Login"}
              </button>
              <div className="auth-center">
                <button
                  type="button"
                  className="auth-underline-link"
                  onClick={() => {
                    setAuthMode("mobile");
                    setSubmitted(false);
                  }}
                >
                  Login By Mobile
                </button>
              </div>
            </div>
          ) : (
            <div>
              {mobileApiError && <div className="auth-error-banner">{mobileApiError}</div>}
              <div className="mobile-row">
                <div className="country-code-field">
                  <span className="country-code-field__label">Country Code</span>
                  <select className="country-code-field__select" value={cc} onChange={(e) => setCc(e.target.value)} disabled={otpSent}>
                    {CC_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mobile-row__input">
                  <input
                    className={mobileSubmitted && !mobileOk ? "input-invalid" : ""}
                    inputMode="numeric"
                    placeholder="Mobile No."
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value.replace(/[^0-9 ]/g, ""))}
                    readOnly={otpSent}
                  />
                  {mobileSubmitted && !mobileOk && <span className="field-error">Enter a valid 10-digit mobile number starting with 9</span>}
                </div>
              </div>

              {otpSent && (
                <div className="otp-field">
                  <input
                    className={mobileSubmitted && !otpOk ? "input-invalid" : ""}
                    inputMode="numeric"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                  {mobileSubmitted && !otpOk && <span className="field-error">Enter the 6-digit OTP</span>}
                </div>
              )}

              {otpSent ? (
                <button type="button" className="btn-primary" onClick={submitVerifyOtp} disabled={mobileSubmitting}>
                  {mobileSubmitting ? "Verifying…" : "Login"}
                </button>
              ) : (
                <button type="button" className="btn-primary" onClick={submitMobile} disabled={mobileSubmitting}>
                  {mobileSubmitting ? "Sending OTP…" : "Get OTP"}
                </button>
              )}

              <div className="auth-center">
                {otpSent ? (
                  <button
                    type="button"
                    className="auth-underline-link"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp("");
                      setMobileSubmitted(false);
                      setMobileApiError("");
                    }}
                  >
                    Change Mobile Number
                  </button>
                ) : (
                  <button
                    type="button"
                    className="auth-underline-link"
                    onClick={() => {
                      setAuthMode("email");
                      setSubmitted(false);
                    }}
                  >
                    Login By Email
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="auth-footnote">Application creation only. Tracking and bank submission continue in the Mobile SFA app.</div>
      </div>
    </div>
  );
}
